/**
 * Knowledge Base Management Component
 * Upload and manage documents for ILO learning
 * WITH HELP MODAL
 * 
 * @page
 */

import React, { useState, useEffect } from 'react';
import {
  Upload, FileText, Trash2, Search, Filter, BarChart3,
  CheckCircle, XCircle, Loader2, Download, ArrowLeft,
  BookOpen, Scale, FileCheck, AlertTriangle, HelpCircle, X,
  Database, Brain, Link, Zap
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Document type categories with icons
const DOCUMENT_TYPES = [
  { value: 'regulation', label: 'Regulatory Document', icon: Scale, color: 'text-red-600' },
  { value: 'sop', label: 'Standard Operating Procedure', icon: FileCheck, color: 'text-blue-600' },
  { value: 'protocol', label: 'Protocol/Template', icon: FileText, color: 'text-green-600' },
  { value: 'training', label: 'Training Material', icon: BookOpen, color: 'text-purple-600' },
  { value: 'guideline', label: 'Guidance/Guideline', icon: AlertTriangle, color: 'text-yellow-600' },
  { value: 'reference', label: 'Reference Material', icon: FileText, color: 'text-gray-600' }
];

const CATEGORIES = [
  'GCP/Regulatory',
  'Safety/Pharmacovigilance',
  'Data Management',
  'Quality Assurance',
  'Clinical Operations',
  'Protocol Development',
  'Site Management',
  'Monitoring',
  'Audit/Inspection',
  'Training',
  'Other'
];

const KnowledgeBaseManager = () => {
  const navigate = useNavigate();
  
  // State
  const [documents, setDocuments] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  
  // Upload form state
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadData, setUploadData] = useState({
    document_type: '',
    document_title: '',
    category: '',
    description: '',
    effective_date: '',
    authority_level: 'standard',
    tags: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);

  /**
   * Load documents on mount
   */
  useEffect(() => {
    loadDocuments();
    loadStatistics();
  }, [filterType, filterCategory, searchTerm]);

  /**
   * Load documents from API
   */
  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filterType) params.append('document_type', filterType);
      if (filterCategory) params.append('category', filterCategory);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(
        `${API_BASE_URL}/knowledge/documents?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setDocuments(response.data.data);
      }
    } catch (error) {
      console.error('Load documents error:', error);
      alert('Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Load statistics
   */
  const loadStatistics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/knowledge/statistics`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Load statistics error:', error);
    }
  };

  /**
   * Handle file selection
   */
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'text/plain', 'text/html', 
        'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      
      if (!allowedTypes.includes(file.type)) {
        alert('Invalid file type. Please upload PDF, TXT, HTML, DOC, or DOCX files.');
        return;
      }
      
      // Max 50MB
      if (file.size > 50 * 1024 * 1024) {
        alert('File too large. Maximum size is 50MB.');
        return;
      }
      
      setSelectedFile(file);
    }
  };

  /**
   * Handle upload
   */
  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      alert('Please select a file');
      return;
    }
    
    if (!uploadData.document_type || !uploadData.document_title || !uploadData.category) {
      alert('Please fill in all required fields');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      Object.entries(uploadData).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });

      const response = await axios.post(
        `${API_BASE_URL}/knowledge/documents`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        }
      );

      if (response.data.success) {
        alert('Document uploaded successfully!');
        resetUploadForm();
        setShowUploadForm(false);
        loadDocuments();
        loadStatistics();
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert(error.response?.data?.message || 'Failed to upload document');
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };

  /**
   * Reset upload form
   */
  const resetUploadForm = () => {
    setUploadData({
      document_type: '',
      document_title: '',
      category: '',
      description: '',
      effective_date: '',
      authority_level: 'standard',
      tags: ''
    });
    setSelectedFile(null);
  };

  /**
   * Delete document
   */
  const handleDelete = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_BASE_URL}/knowledge/documents/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert('Document deleted successfully');
        loadDocuments();
        loadStatistics();
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete document');
    }
  };

  /**
   * Format file size
   */
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // ========================================================================
  // HELP MODAL
  // ========================================================================

  const HelpModal = () => {
    if (!showHelp) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-[#475569] to-[#E91E63] text-white px-6 py-4 flex items-center justify-between rounded-t-xl">
            <div className="flex items-center gap-3">
              <Database className="w-6 h-6" />
              <h3 className="font-bold text-xl">Knowledge Base Guide</h3>
            </div>
            <button 
              onClick={() => setShowHelp(false)}
              className="hover:bg-white/20 rounded-lg p-2 transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            
            {/* What is Knowledge Base */}
            <section>
              <h4 className="font-bold text-gray-900 text-lg mb-3">What is Knowledge Base?</h4>
              <p className="text-gray-700 mb-3">
                The Knowledge Base is <strong className="text-[#E91E63]">ILO's learning library</strong> - 
                upload documents here to expand what ILO knows and can reference.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="font-bold text-blue-900 mb-1 flex items-center gap-2">
                    <Database className="w-4 h-4" />
                    Store
                  </div>
                  <div className="text-xs text-blue-800">Upload regulations, SOPs, protocols, and guidelines</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="font-bold text-green-900 mb-1 flex items-center gap-2">
                    <Brain className="w-4 h-4" />
                    Learn
                  </div>
                  <div className="text-xs text-green-800">ILO processes and indexes document content</div>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <div className="font-bold text-purple-900 mb-1 flex items-center gap-2">
                    <Link className="w-4 h-4" />
                    Reference
                  </div>
                  <div className="text-xs text-purple-800">ILO cites these documents when answering questions</div>
                </div>
              </div>
            </section>

            {/* Document Types */}
            <section>
              <h4 className="font-bold text-gray-900 text-lg mb-3">Document Types</h4>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2 text-sm">
                  <Scale className="w-4 h-4 text-red-600" />
                  <span><strong>Regulatory</strong> - FDA, ICH, EMA docs</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <FileCheck className="w-4 h-4 text-blue-600" />
                  <span><strong>SOP</strong> - Standard procedures</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <FileText className="w-4 h-4 text-green-600" />
                  <span><strong>Protocol</strong> - Templates, forms</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <BookOpen className="w-4 h-4 text-purple-600" />
                  <span><strong>Training</strong> - Educational content</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <AlertTriangle className="w-4 h-4 text-yellow-600" />
                  <span><strong>Guideline</strong> - Industry guidance</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <FileText className="w-4 h-4 text-gray-600" />
                  <span><strong>Reference</strong> - General materials</span>
                </div>
              </div>
            </section>

            {/* How to Upload */}
            <section>
              <h4 className="font-bold text-gray-900 text-lg mb-3">How to Upload</h4>
              <ol className="space-y-2 text-gray-700">
                <li className="flex gap-3">
                  <span className="font-bold text-[#E91E63] flex-shrink-0">1.</span>
                  <span>Click <strong>"Upload Document"</strong> button in the header</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-[#E91E63] flex-shrink-0">2.</span>
                  <span>Select document type (Regulatory, SOP, etc.)</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-[#E91E63] flex-shrink-0">3.</span>
                  <span>Enter title and select category</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-[#E91E63] flex-shrink-0">4.</span>
                  <span>Choose your file (PDF, DOC, TXT, HTML - max 50MB)</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold text-[#E91E63] flex-shrink-0">5.</span>
                  <span>Click <strong>"Upload"</strong> - ILO will process it automatically</span>
                </li>
              </ol>
            </section>

            {/* Suggested Documents */}
            <section>
              <h4 className="font-bold text-gray-900 text-lg mb-3">📋 Suggested Documents to Upload</h4>
              <div className="space-y-2">
                <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="font-medium text-gray-900">ICH E6(R2) GCP Guidelines</div>
                  <div className="text-xs text-gray-500 mt-1">Core regulatory reference for clinical trials</div>
                </div>
                <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="font-medium text-gray-900">21 CFR Part 312 (IND Regulations)</div>
                  <div className="text-xs text-gray-500 mt-1">FDA investigational drug requirements</div>
                </div>
                <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="font-medium text-gray-900">Company-Specific Protocol Templates</div>
                  <div className="text-xs text-gray-500 mt-1">Your organization's standard templates</div>
                </div>
              </div>
            </section>

            {/* Tips */}
            <section>
              <h4 className="font-bold text-gray-900 text-lg mb-3">💡 Pro Tips</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex gap-2">
                  <span className="text-[#E91E63]">•</span>
                  <span>Use descriptive titles - "ICH E6(R2) GCP" not "document1.pdf"</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#E91E63]">•</span>
                  <span>Add tags for better searchability (comma-separated)</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#E91E63]">•</span>
                  <span>Set authority level: "High" for regulations, "Standard" for internal docs</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#E91E63]">•</span>
                  <span>PDF works best - text is extracted automatically</span>
                </li>
              </ul>
            </section>

            {/* Connection to ILO */}
            <section className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                <Zap className="w-4 h-4" />
                <span>How it connects to ILO</span>
              </h4>
              <p className="text-sm text-blue-800">
                Documents uploaded here become part of ILO's knowledge. When you ask ILO a question, 
                it searches this Knowledge Base and cites relevant documents in its answers. 
                More documents = smarter ILO.
              </p>
            </section>

            {/* FDA Compliance */}
            <section className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-bold text-green-900 mb-2 flex items-center gap-2">
                <span>🛡️</span>
                <span>Designed for FDA 21 CFR Part 11</span>
              </h4>
              <p className="text-sm text-green-800">
                This system is designed with 21 CFR Part 11 principles in mind. All uploads 
                are logged with audit trails including user, timestamp, and document checksums. 
                Formal validation pending.
              </p>
            </section>

          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Help Modal */}
      <HelpModal />

      {/* Header */}
      <header className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              
              <BookOpen className="w-8 h-8" />
              <div>
                <h1 className="text-2xl font-bold">Knowledge Base Manager</h1>
                <p className="text-sm opacity-90">Upload documents to enhance ILO's learning</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowHelp(true)}
                className="px-4 py-2 border border-white/30 hover:bg-white/10 rounded-lg transition-colors flex items-center gap-2"
              >
                <HelpCircle className="w-4 h-4" />
                Help
              </button>
              
              <button
                onClick={() => setShowUploadForm(!showUploadForm)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Upload Document
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Statistics Cards */}
        {statistics && statistics.totals && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Documents</p>
                  <p className="text-3xl font-bold text-[#475569]">
                    {statistics.totals.total_documents || 0}
                  </p>
                </div>
                <FileText className="w-12 h-12 text-[#475569] opacity-20" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Size</p>
                  <p className="text-3xl font-bold text-[#475569]">
                    {formatFileSize(statistics.totals.total_size || 0)}
                  </p>
                </div>
                <BarChart3 className="w-12 h-12 text-[#475569] opacity-20" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Categories</p>
                  <p className="text-3xl font-bold text-[#475569]">
                    {statistics.by_category?.length || 0}
                  </p>
                </div>
                <Filter className="w-12 h-12 text-[#475569] opacity-20" />
              </div>
            </div>
          </div>
        )}

        {/* Upload Form */}
        {showUploadForm && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Upload New Document</h2>
            
            <form onSubmit={handleUpload} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Document Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Document Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={uploadData.document_type}
                    onChange={(e) => setUploadData({...uploadData, document_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#475569] focus:border-transparent"
                    required
                  >
                    <option value="">Select type...</option>
                    {DOCUMENT_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={uploadData.category}
                    onChange={(e) => setUploadData({...uploadData, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#475569] focus:border-transparent"
                    required
                  >
                    <option value="">Select category...</option>
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Title */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Document Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={uploadData.document_title}
                    onChange={(e) => setUploadData({...uploadData, document_title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#475569] focus:border-transparent"
                    placeholder="e.g., ICH E6(R2) Good Clinical Practice Guidelines"
                    required
                  />
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={uploadData.description}
                    onChange={(e) => setUploadData({...uploadData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#475569] focus:border-transparent"
                    rows={2}
                    placeholder="Brief description of the document content..."
                  />
                </div>

                {/* Authority Level */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Authority Level
                  </label>
                  <select
                    value={uploadData.authority_level}
                    onChange={(e) => setUploadData({...uploadData, authority_level: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#475569] focus:border-transparent"
                  >
                    <option value="high">High (Regulations, Laws)</option>
                    <option value="standard">Standard (SOPs, Guidelines)</option>
                    <option value="reference">Reference (General Info)</option>
                  </select>
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tags (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={uploadData.tags}
                    onChange={(e) => setUploadData({...uploadData, tags: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#475569] focus:border-transparent"
                    placeholder="e.g., GCP, FDA, clinical trials"
                  />
                </div>

                {/* File Upload */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    File <span className="text-red-500">*</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#475569] transition-colors">
                    <input
                      type="file"
                      onChange={handleFileSelect}
                      accept=".pdf,.txt,.html,.doc,.docx"
                      className="hidden"
                      id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      {selectedFile ? (
                        <div className="flex items-center justify-center gap-2">
                          <FileText className="w-6 h-6 text-[#475569]" />
                          <span className="font-medium">{selectedFile.name}</span>
                          <span className="text-sm text-gray-500">
                            ({formatFileSize(selectedFile.size)})
                          </span>
                        </div>
                      ) : (
                        <div>
                          <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-gray-600">Click to select file</p>
                          <p className="text-xs text-gray-400 mt-1">PDF, DOC, DOCX, TXT, HTML (max 50MB)</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              </div>

              {/* Upload Progress */}
              {uploadProgress !== null && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-[#E91E63] h-2 rounded-full transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadForm(false);
                    resetUploadForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUploading || !selectedFile}
                  className="px-4 py-2 bg-gradient-to-r from-[#475569] to-[#334155] text-white rounded-lg hover:from-[#334155] hover:to-[#1e293b] transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Upload
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#475569] focus:border-transparent"
                />
              </div>
            </div>

            {/* Type Filter */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#475569] focus:border-transparent"
            >
              <option value="">All Types</option>
              {DOCUMENT_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>

            {/* Category Filter */}
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#475569] focus:border-transparent"
            >
              <option value="">All Categories</option>
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Documents Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#475569]" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-2">No documents found</p>
              <p className="text-sm text-gray-400">Upload your first document to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Document
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Uploaded
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {documents.map((doc) => {
                    const typeInfo = DOCUMENT_TYPES.find(t => t.value === doc.document_type) || {};
                    const TypeIcon = typeInfo.icon || FileText;
                    
                    return (
                      <tr key={doc.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <TypeIcon className={`w-5 h-5 ${typeInfo.color || 'text-gray-600'}`} />
                            <div>
                              <div className="font-medium text-gray-900">
                                {doc.document_title}
                              </div>
                              {doc.description && (
                                <div className="text-sm text-gray-500 truncate max-w-xs">
                                  {doc.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {typeInfo.label || doc.document_type}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {doc.category}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {formatFileSize(doc.file_size)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {new Date(doc.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handleDelete(doc.id, doc.document_title)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete document"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBaseManager;
